import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '@contexts/AppContext';
import { useAuth } from '@contexts/AuthContext';
import { useCart } from '@contexts/CartContext';
import useMediaQuery from '@hooks/useMediaQuery';
import useSearch from '@hooks/useSearch';
import TypeAheadSearch from '@components/search/TypeAheadSearch';
import { getAllProducts } from '@services/dataService';
import DataSourceBadge from '@components/common/DataSourceBadge';

/**
 * Header — Production version (A5a)
 *
 * White bg with border-b border-n2. Sticky.
 * Layout: Hamburger | Sandvik SVG logotype | Search bar + Electric Blue button | Cart badge | Language flag | Avatar
 * Mobile: Search moves below main row.
 *
 * User dropdown: Profile, Favourites, Sign out ONLY.
 * Account-level pages (Orders, Cases, Invoices, Reports, Track data)
 * are in SecondaryNav, matching the live app pattern.
 */

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: 'sv', label: 'Svenska', flag: '\u{1F1F8}\u{1F1EA}' },
  { code: 'de', label: 'Deutsch', flag: '\u{1F1E9}\u{1F1EA}' },
  { code: 'fr', label: 'Français', flag: '\u{1F1EB}\u{1F1F7}' },
  { code: 'es', label: 'Español', flag: '\u{1F1EA}\u{1F1F8}' },
  { code: 'pt', label: 'Português', flag: '\u{1F1E7}\u{1F1F7}' },
  { code: 'fi', label: 'Suomi', flag: '\u{1F1EB}\u{1F1EE}' },
  { code: 'zh', label: '中文', flag: '\u{1F1E8}\u{1F1F3}' },
];

export default function Header() {
  const { state, dispatch } = useApp();
  const { isAuthenticated, userType, account, logout } = useAuth();
  const { itemCount } = useCart();
  const { isMobile } = useMediaQuery();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [allProducts, setAllProducts] = useState([]);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  const langRef = useRef(null);
  const profileRef = useRef(null);

  useEffect(() => {
    getAllProducts().then(setAllProducts).catch(() => setAllProducts([]));
  }, []);

  const { suggestions, isSearching } = useSearch(searchQuery, { products: allProducts });

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangDropdownOpen(false);
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setLangDropdownOpen(false);
        setProfileDropdownOpen(false);
        setMobileSearchOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleSearchSubmit = useCallback((q) => {
    const term = typeof q === 'string' ? q : searchQuery;
    if (term.trim().length >= 2) {
      navigate(`/search?q=${encodeURIComponent(term.trim())}`);
      setSearchQuery('');
      setMobileSearchOpen(false);
    }
  }, [searchQuery, navigate]);

  const handleSearchSelect = useCallback((suggestion) => {
    if (suggestion?.product?.itemCode) {
      navigate(`/product/${suggestion.product.itemCode}`);
      setSearchQuery('');
      setMobileSearchOpen(false);
    }
  }, [navigate]);

  const handleLanguageSelect = useCallback((code) => {
    dispatch({ type: 'SET_LANGUAGE', payload: code });
    setLangDropdownOpen(false);
  }, [dispatch]);

  const currentLang = LANGUAGES.find(l => l.code === state.language) || LANGUAGES[0];

  const getUserInitials = () => {
    if (!isAuthenticated || !account) return null;
    const name = account.name || account.email || '';
    const parts = name.split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.charAt(0).toUpperCase();
  };

  const getUserDisplayName = () => {
    if (!isAuthenticated || !account) return '';
    return account.nickname || account.name || account.email || '';
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-n2">
      <div className="sandvik-container">
        <div className="flex items-center gap-3 h-16">
          <button onClick={() => dispatch({ type: 'TOGGLE_DRAWER' })}
            className="p-2 -ml-2 min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-n1 transition-colors" aria-label="Open navigation menu">
            <span className="material-symbols-outlined text-2xl text-carbon-black">menu</span>
          </button>

          <Link to="/" className="flex-shrink-0" aria-label="My Rock Tools home">
            <img src="/logos/sandvik_logotype_black_rgb.svg" alt="Sandvik" className="h-5 sm:h-6 w-auto" />
          </Link>

          <div className="hidden md:block flex-1 max-w-xl mx-4">
            <TypeAheadSearch value={searchQuery} onChange={setSearchQuery}
              onSearch={handleSearchSubmit} onSelect={handleSearchSelect}
              suggestions={suggestions} loading={isSearching} placeholder="Search by product" />
          </div>

          <div className="flex-1 md:hidden" />

          <div className="flex items-center gap-1">
            {/* U2 Piece A Leaf 1c Step 2 — data-source mode indicator */}
            <div className="hidden md:inline-flex mr-2">
              <DataSourceBadge />
            </div>

            <button onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
              className="md:hidden p-2 min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-n1 transition-colors" aria-label="Search">
              <span className="material-symbols-outlined text-xl text-carbon-black">search</span>
            </button>

            <Link to="/cart" className="relative p-2 min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-n1 transition-colors"
              aria-label={`Shopping cart${itemCount > 0 ? `, ${itemCount} items` : ''}`}>
              <span className="material-symbols-outlined text-xl text-carbon-black">shopping_cart</span>
              {itemCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-electric-blue text-white text-[10px] font-normal rounded-full flex items-center justify-center px-1">
                  {itemCount > 99 ? '99+' : itemCount}
                </span>
              )}
            </Link>

            <div className="relative" ref={langRef}>
              <button onClick={() => { setLangDropdownOpen(!langDropdownOpen); setProfileDropdownOpen(false); }}
                className="p-2 min-h-[44px] min-w-[44px] hover:bg-n1 transition-colors flex items-center gap-1"
                aria-label={`Language: ${currentLang.label}`} aria-expanded={langDropdownOpen}>
                <span className="text-base leading-none">{currentLang.flag}</span>
                <span className="material-symbols-outlined text-sm text-n5">expand_more</span>
              </button>
              {langDropdownOpen && (
                <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-n2 shadow-lg z-50 py-1">
                  {LANGUAGES.map((lang) => (
                    <button key={lang.code} onClick={() => handleLanguageSelect(lang.code)}
                      className={`w-full flex items-center gap-3 px-4 py-2 text-sm text-left hover:bg-n1 transition-colors font-sandvik-text
                        ${state.language === lang.code ? 'text-electric-blue font-normal' : 'text-carbon-black'}`}>
                      <span>{lang.flag}</span>
                      <span>{lang.label}</span>
                      {state.language === lang.code && (
                        <span className="material-symbols-outlined text-electric-blue text-sm ml-auto">check</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="hidden sm:block w-px h-6 bg-n2 mx-1" />

            <div className="relative" ref={profileRef}>
              {isAuthenticated ? (
                <button onClick={() => { setProfileDropdownOpen(!profileDropdownOpen); setLangDropdownOpen(false); }}
                  className="p-1 min-h-[44px] min-w-[44px] hover:bg-n1 transition-colors flex items-center gap-2"
                  aria-label="Account menu" aria-expanded={profileDropdownOpen}>
                  <div className="w-8 h-8 bg-electric-blue text-white text-xs font-normal rounded-full flex items-center justify-center font-sandvik-text">
                    {getUserInitials() || '?'}
                  </div>
                  <span className="hidden lg:inline text-sm font-sandvik-text text-carbon-black max-w-[120px] truncate">
                    {getUserDisplayName()}
                  </span>
                </button>
              ) : (
                <button onClick={() => { setProfileDropdownOpen(!profileDropdownOpen); setLangDropdownOpen(false); }}
                  className="p-2 min-h-[44px] min-w-[44px] hover:bg-n1 transition-colors flex items-center gap-1"
                  aria-label="Sign in or preferences" aria-expanded={profileDropdownOpen}>
                  <span className="material-symbols-outlined text-xl text-n5">account_circle</span>
                  <span className="material-symbols-outlined text-sm text-n5">expand_more</span>
                </button>
              )}

              {profileDropdownOpen && (
                <div className="absolute right-0 top-full mt-1 w-64 bg-white border border-n2 shadow-lg z-50 py-3 px-4">
                  {isAuthenticated ? (
                    <>
                      <div className="mb-3 pb-3 border-b border-n2">
                        <p className="text-sm font-normal font-sandvik-text text-carbon-black">{account?.name || 'User'}</p>
                        {(account?.company || account?.customerName) && (
                          <p className="text-xs text-n5 font-sandvik-text">{account.company || account.customerName}</p>
                        )}
                        <p className="text-xs text-n5 font-sandvik-text">{account?.email || ''}</p>
                        {userType && (
                          <span className="inline-block mt-1 text-[10px] font-normal px-2 py-0.5 bg-n1 text-n6 font-sandvik-text">
                            {userType}
                          </span>
                        )}
                      </div>
                      <Link to="/profile" onClick={() => setProfileDropdownOpen(false)}
                        className="flex items-center gap-2 py-2 text-sm text-carbon-black hover:text-electric-blue transition-colors font-sandvik-text">
                        <span className="material-symbols-outlined text-lg">person</span>
                        My profile
                      </Link>
                      <Link to="/favourites" onClick={() => setProfileDropdownOpen(false)}
                        className="flex items-center gap-2 py-2 text-sm text-carbon-black hover:text-electric-blue transition-colors font-sandvik-text">
                        <span className="material-symbols-outlined text-lg">bookmark</span>
                        Favourites
                      </Link>
                      <div className="mt-3 pt-3 border-t border-n2">
                        <button onClick={() => { setProfileDropdownOpen(false); logout(); }}
                          className="flex items-center gap-2 py-2 text-sm text-n6 hover:text-electric-blue transition-colors font-sandvik-text w-full">
                          <span className="material-symbols-outlined text-lg">logout</span>
                          Sign out
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <Link to="/login" onClick={() => setProfileDropdownOpen(false)}
                        className="block w-full bg-electric-blue hover:bg-eb-3 text-white text-sm font-normal text-center py-2.5 px-4 rounded-none transition-colors font-sandvik-text mb-3">
                        Sign in
                      </Link>
                      <div className="mb-3">
                        <p className="text-xs text-n5 font-sandvik-text tracking-wide mb-1.5">Units of measure</p>
                        <div className="flex border border-n2">
                          <button onClick={() => dispatch({ type: 'SET_UOM', payload: 'metric' })}
                            className={`flex-1 py-1.5 text-xs font-normal font-sandvik-text transition-colors
                              ${state.uom === 'metric' ? 'bg-vibrant-orange text-white' : 'bg-white text-n6 hover:bg-n1'}`}>
                            mm / kg
                          </button>
                          <button onClick={() => dispatch({ type: 'SET_UOM', payload: 'imperial' })}
                            className={`flex-1 py-1.5 text-xs font-normal font-sandvik-text transition-colors
                              ${state.uom === 'imperial' ? 'bg-vibrant-orange text-white' : 'bg-white text-n6 hover:bg-n1'}`}>
                            in / lb
                          </button>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-n5 font-sandvik-text tracking-wide mb-1.5">Language</p>
                        <select value={state.language} onChange={(e) => handleLanguageSelect(e.target.value)}
                          className="w-full px-3 py-1.5 text-sm border border-n2 rounded-none font-sandvik-text text-carbon-black focus:outline-none focus:border-electric-blue">
                          {LANGUAGES.map((lang) => (
                            <option key={lang.code} value={lang.code}>{lang.flag} {lang.label}</option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {mobileSearchOpen && (
          <div className="md:hidden pb-3">
            <TypeAheadSearch value={searchQuery} onChange={setSearchQuery}
              onSearch={handleSearchSubmit} onSelect={handleSearchSelect}
              suggestions={suggestions} loading={isSearching} placeholder="Search products..." />
          </div>
        )}
      </div>
    </header>
  );
}
